import { useState, useEffect, useMemo } from 'react';
import { 
  PlusIcon, 
  XMarkIcon, 
  LinkIcon,
  TableCellsIcon,
  ChartBarIcon,
  RectangleStackIcon,
  ChevronDownIcon,
  ArrowsRightLeftIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

/**
 * MultiSourceSelector Component
 * 
 * Allows users to select one or multiple data sources for chart building.
 * Shows related tables based on relationships and provides join hints.
 */
export default function MultiSourceSelector({
  dataSources = [],
  views = [],
  relationships = [],
  selected = [],
  onChange,
  mode = 'single', // 'single', 'multi', or 'auto'
  showRelationships = true,
  className = ''
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Combine all sources with category labels
  const allSources = useMemo(() => {
    const sources = [];
    
    // Add views first (preferred - they have pre-joined data)
    views.forEach(v => {
      sources.push({
        id: v._id || `view_${v.name}`,
        name: v.name,
        type: 'view',
        category: 'Views',
        icon: RectangleStackIcon,
        description: v.description || `Joined view from ${v.baseTable}`,
        tableName: v.bigQueryViewName || `view_${v.name?.toLowerCase().replace(/\s+/g, '_')}`,
        baseTable: v.baseTable,
        includedTables: v.includedTables?.map(t => t.table) || [],
        calculatedColumns: v.calculatedColumns || []
      });
    });
    
    // Add regular data sources
    dataSources.forEach(ds => {
      sources.push({
        id: ds._id,
        name: ds.name,
        type: 'datasource',
        category: 'Data Sources',
        icon: TableCellsIcon,
        description: ds.description || `${ds.schemaInfo?.tables?.[0]?.columns?.length || 0} columns`,
        tableName: ds.bigQueryTable,
        columns: ds.schemaInfo?.tables?.[0]?.columns || []
      });
    });
    
    return sources;
  }, [dataSources, views]);
  
  // Find related sources based on relationships
  const getRelatedSources = useMemo(() => {
    if (selected.length === 0 || !relationships.length) return [];
    
    const selectedTables = selected
      .map(id => allSources.find(s => s.id === id)?.tableName)
      .filter(Boolean);
    
    const relatedTableNames = new Set();
    
    relationships.forEach(rel => {
      if (rel.isActive !== false) {
        selectedTables.forEach(table => {
          if (rel.sourceTable === table) {
            relatedTableNames.add(rel.targetTable);
          } else if (rel.targetTable === table) {
            relatedTableNames.add(rel.sourceTable);
          }
        });
      }
    });
    
    // Find sources that match related table names
    return allSources.filter(s => 
      relatedTableNames.has(s.tableName) && !selected.includes(s.id)
    );
  }, [selected, relationships, allSources]);
  
  // Filter sources by search and category
  const filteredSources = useMemo(() => {
    return allSources.filter(source => {
      const matchesSearch = !searchTerm || 
        source.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        source.tableName?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = selectedCategory === 'all' || source.type === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [allSources, searchTerm, selectedCategory]);
  
  // Get selected sources with full info
  const selectedSources = useMemo(() => {
    return selected.map(id => allSources.find(s => s.id === id)).filter(Boolean);
  }, [selected, allSources]);
  
  // Get join information between selected sources
  const joinInfo = useMemo(() => {
    if (selectedSources.length < 2 || !relationships.length) return [];
    
    const joins = [];
    const selectedTables = selectedSources.map(s => s.tableName);
    
    for (let i = 0; i < selectedTables.length; i++) {
      for (let j = i + 1; j < selectedTables.length; j++) {
        const table1 = selectedTables[i];
        const table2 = selectedTables[j];
        
        const rel = relationships.find(r => 
          (r.sourceTable === table1 && r.targetTable === table2) ||
          (r.sourceTable === table2 && r.targetTable === table1)
        );
        
        if (rel) {
          joins.push({
            from: table1,
            to: table2,
            fromColumn: rel.sourceTable === table1 ? rel.sourceColumn : rel.targetColumn,
            toColumn: rel.sourceTable === table1 ? rel.targetColumn : rel.sourceColumn,
            type: rel.type,
            confidence: rel.confidence
          });
        }
      }
    }
    
    return joins;
  }, [selectedSources, relationships]);
  
  // Handle source selection
  const handleSelect = (sourceId) => {
    if (mode === 'single') {
      onChange([sourceId]);
    } else {
      if (selected.includes(sourceId)) {
        onChange(selected.filter(id => id !== sourceId));
      } else {
        onChange([...selected, sourceId]);
      }
    }
  };
  
  // Remove a selected source
  const handleRemove = (sourceId) => {
    onChange(selected.filter(id => id !== sourceId));
  };
  
  // Get icon for source type
  const getSourceIcon = (type) => {
    switch (type) {
      case 'view': return RectangleStackIcon;
      default: return TableCellsIcon;
    }
  };
  
  // Get badge color for source type
  const getTypeBadgeClass = (type) => {
    switch (type) {
      case 'view': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <div className={`${className}`}>
      {/* Selected Sources Display */}
      {selectedSources.length > 0 && (
        <div className="mb-3">
          <div className="flex flex-wrap gap-2">
            {selectedSources.map((source, index) => {
              const Icon = getSourceIcon(source.type);
              return (
                <div 
                  key={source.id}
                  className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-700 rounded-lg"
                >
                  <Icon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                    {index === 0 && mode === 'multi' && (
                      <span className="text-xs text-primary-500 mr-1">[Primary]</span>
                    )}
                    {source.name}
                  </span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${getTypeBadgeClass(source.type)}`}>
                    {source.type}
                  </span>
                  {mode === 'multi' && (
                    <button
                      onClick={() => handleRemove(source.id)}
                      className="ml-1 text-primary-400 hover:text-primary-600"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Join Information */}
          {joinInfo.length > 0 && showRelationships && (
            <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-1.5 text-xs text-green-700 dark:text-green-300 mb-1">
                <LinkIcon className="w-3.5 h-3.5" />
                <span className="font-medium">Join Path Detected</span>
              </div>
              <div className="space-y-1">
                {joinInfo.map((join, i) => (
                  <div key={i} className="text-xs text-green-600 dark:text-green-400 font-mono">
                    {join.from}.{join.fromColumn} → {join.to}.{join.toColumn}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Warning if no join path */}
          {selectedSources.length > 1 && joinInfo.length === 0 && showRelationships && (
            <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                <InformationCircleIcon className="w-3.5 h-3.5" />
                <span>No relationship found between these tables. AI will attempt to determine the join.</span>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Dropdown Trigger */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-white dark:bg-surface-800 border border-surface-300 dark:border-surface-600 rounded-lg hover:border-primary-400 dark:hover:border-primary-500 transition-colors"
      >
        <div className="flex items-center gap-2">
          <TableCellsIcon className="w-5 h-5 text-surface-400" />
          <span className="text-sm text-surface-600 dark:text-surface-300">
            {selectedSources.length === 0 
              ? 'Select data source(s)...'
              : mode === 'multi' 
                ? `${selectedSources.length} source${selectedSources.length > 1 ? 's' : ''} selected`
                : 'Change data source'
            }
          </span>
        </div>
        <ChevronDownIcon className={`w-5 h-5 text-surface-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Expanded Dropdown */}
      {isExpanded && (
        <div className="mt-2 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl shadow-lg overflow-hidden">
          {/* Search and Filter */}
          <div className="p-3 border-b border-surface-200 dark:border-surface-700">
            <input
              type="text"
              placeholder="Search sources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-surface-300 dark:border-surface-600 rounded-lg bg-white dark:bg-surface-900 focus:ring-2 focus:ring-primary-500"
            />
            
            <div className="flex gap-2 mt-2">
              {['all', 'view', 'datasource'].map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                    selectedCategory === cat
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                      : 'bg-surface-100 text-surface-600 dark:bg-surface-700 dark:text-surface-400 hover:bg-surface-200'
                  }`}
                >
                  {cat === 'all' ? 'All' : cat === 'view' ? '🔗 Views' : '📊 Data Sources'}
                </button>
              ))}
            </div>
          </div>
          
          {/* Related Sources Suggestion */}
          {getRelatedSources.length > 0 && mode === 'multi' && (
            <div className="px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-surface-200 dark:border-surface-700">
              <div className="flex items-center gap-1.5 text-xs text-blue-700 dark:text-blue-300 mb-2">
                <ArrowsRightLeftIcon className="w-3.5 h-3.5" />
                <span className="font-medium">Related Tables (can be joined)</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {getRelatedSources.slice(0, 5).map(source => (
                  <button
                    key={source.id}
                    onClick={() => handleSelect(source.id)}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-white dark:bg-surface-800 border border-blue-200 dark:border-blue-700 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30"
                  >
                    <PlusIcon className="w-3 h-3" />
                    {source.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Source List */}
          <div className="max-h-64 overflow-y-auto">
            {filteredSources.length === 0 ? (
              <div className="p-4 text-center text-sm text-surface-500">
                No sources found matching your search.
              </div>
            ) : (
              <div className="divide-y divide-surface-100 dark:divide-surface-700">
                {filteredSources.map(source => {
                  const Icon = source.icon;
                  const isSelected = selected.includes(source.id);
                  
                  return (
                    <button
                      key={source.id}
                      onClick={() => handleSelect(source.id)}
                      className={`w-full flex items-start gap-3 p-3 text-left hover:bg-surface-50 dark:hover:bg-surface-700/50 transition-colors ${
                        isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${
                        source.type === 'view'
                          ? 'bg-blue-100 dark:bg-blue-900/40'
                          : 'bg-surface-100 dark:bg-surface-700'
                      }`}>
                        <Icon className={`w-5 h-5 ${
                          source.type === 'view'
                            ? 'text-blue-600 dark:text-blue-400'
                            : 'text-surface-600 dark:text-surface-400'
                        }`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-surface-900 dark:text-white truncate">
                            {source.name}
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${getTypeBadgeClass(source.type)}`}>
                            {source.type}
                          </span>
                          {isSelected && (
                            <span className="text-xs text-primary-600 dark:text-primary-400">✓</span>
                          )}
                        </div>
                        <p className="text-xs text-surface-500 dark:text-surface-400 truncate mt-0.5">
                          {source.description}
                        </p>
                        <p className="text-xs text-surface-400 dark:text-surface-500 font-mono truncate mt-0.5">
                          {source.tableName}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Multi-select hint */}
          {mode === 'multi' && (
            <div className="p-2 bg-surface-50 dark:bg-surface-900 border-t border-surface-200 dark:border-surface-700">
              <p className="text-xs text-surface-500 text-center">
                💡 Click to toggle selection. First selected will be the primary table.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

